'use client';

import { type JSX } from 'react';

import { useKeyPressEvent } from 'react-use';

import Button from '@/components/ui/Button';
import Grid from '@/components/ui/Grid';
import HStack from '@/components/ui/HStack';
import Link from '@/components/ui/Link';
import NewTextInput from '@/components/ui/NewTextInput';
import Text from '@/components/ui/Text';
import VStack from '@/components/ui/VStack';
import appIcons from '@/constants/appIcons';
import NewCountriesDropdown from '@/features/countries/components/NewCountriesDropdown';
import type { Country } from '@/features/countries/types/Country';
import FleetSizeDropdown from '@/features/fleetSizes/components/FleetSizeDropdown';
import type { FleetSize } from '@/features/fleetSizes/types/FleetSize';
import IndustriesDropdown from '@/features/industries/components/IndustriesDropdown';
import type { Industry } from '@/features/industries/types/Industry';
import useI18n from '@/hooks/useI18n';

import useRegister from '../hooks/useRegister';
import { authRoutes } from '../routes/authRoutes';

export default function RegisterForm(): JSX.Element {
    // ----------------------------------------------------------------------------------------------------
    // MARK: States & Constants
    // ----------------------------------------------------------------------------------------------------
    const { t } = useI18n();

    const { isValid, isLoading, error, validationErrors, submit, register, setValue, watch } = useRegister();
    const { country, industry, fleetSize } = watch();

    // ----------------------------------------------------------------------------------------------------
    // MARK: Functions
    // ----------------------------------------------------------------------------------------------------
    function setCountry(newCountry: Country | null): void {
        setValue('country', newCountry, { shouldValidate: true });
    }

    function setIndustry(newIndustry: Industry | null): void {
        setValue('industry', newIndustry, { shouldValidate: true });
    }

    function setFleetSize(newFleetSize: FleetSize | null): void {
        setValue('fleetSize', newFleetSize, { shouldValidate: true });
    }

    // ----------------------------------------------------------------------------------------------------
    // MARK: Side Effects
    // ----------------------------------------------------------------------------------------------------
    // Press on enter to submit
    useKeyPressEvent('Enter', () => {
        if (isValid && !isLoading) {
            submit();
        }
    });

    // ----------------------------------------------------------------------------------------------------
    // MARK: Main Component UI
    // ----------------------------------------------------------------------------------------------------
    return (
        <div className="space-y-10">
            <Grid columns={{ base: 1, xl: 2, lg: 1 }} gap={3}>
                <NewTextInput
                    register={register('fullName')}
                    type="text"
                    placeholder={t('Auth.Full_Name_Placeholder')}
                    prefixIcon={appIcons.outline.user}
                    errorMessage={validationErrors.fullName?.message}
                    className="col-span-2"
                    isRequired
                />

                <NewTextInput
                    register={register('organizationName')}
                    type="text"
                    placeholder={t('Auth.Company_Name_Placeholder')}
                    prefixIcon={appIcons.outline.company}
                    errorMessage={validationErrors.organizationName?.message}
                    className="col-span-2"
                    isRequired
                />

                <NewTextInput
                    register={register('emailAddress')}
                    type="email"
                    placeholder={t('Auth.Email_Placeholder')}
                    prefixIcon={appIcons.outline.email}
                    errorMessage={validationErrors.emailAddress?.message}
                    className="col-span-2"
                    isRequired
                />

                <NewCountriesDropdown
                    register={register('country')}
                    country={country}
                    setCountry={setCountry}
                    errorMessage={validationErrors.country?.message}
                />

                <NewTextInput
                    register={register('phone')}
                    type="tel"
                    placeholder={t('Auth.Phone_Placeholder')}
                    prefixIcon={!country ? appIcons.outline.call : undefined}
                    prefixText={country ? `+${country.phone_code}` : undefined}
                    errorMessage={validationErrors.phone?.message}
                    isRequired
                />

                <IndustriesDropdown
                    register={register('industry')}
                    industry={industry}
                    setIndustry={setIndustry}
                    errorMessage={validationErrors.industry?.message}
                />

                <FleetSizeDropdown
                    register={register('fleetSize')}
                    fleetSize={fleetSize}
                    setFleetSize={setFleetSize}
                    errorMessage={validationErrors.fleetSize?.message}
                />

                <NewTextInput
                    register={register('password')}
                    type="password"
                    placeholder={t('Auth.Password_Placeholder')}
                    prefixIcon={appIcons.outline.password}
                    errorMessage={validationErrors.password?.message}
                    className="col-span-2"
                    isRequired
                />
            </Grid>

            <VStack vGap={4}>
                <Button verticalPadding="lg" width="full" isLoading={isLoading} onClick={submit}>
                    {t('Common.Submit')}
                </Button>

                {/* Error Message */}
                {error && (
                    <Text size="2xs" color="danger">
                        {error.message}
                    </Text>
                )}
            </VStack>

            <HStack justify="center" className="w-full space-x-1">
                <Text size="sm">{t('Auth.Already_Have_Account')}</Text>
                <Link href={authRoutes.app.login()} color="accent">
                    {t('Auth.Login')}
                </Link>
            </HStack>
        </div>
    );
}
